import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Package, LogOut, Menu, X, Settings2, ChevronsLeft, ChevronsRight, Sun, Moon, AlertTriangle, Search, LayoutGrid, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { canOpen, ruleForUrl, homePath } from '../utils/access';
import { useTableCards } from '../hooks/useTableCards';
import { useTheme } from '../contexts/ThemeContext';
import { useBrand } from '../contexts/BrandContext';
import FullFixLogo from '../assets/FullFixLogo';
import HelpButton from './HelpButton';
import GlobalScan from './GlobalScan';
import SwitchUserButton from './SwitchUser';
import { loginPath } from '../utils/authMode';
import VersionWatcher from './VersionWatcher';
import BalanceBadge from './BalanceBadge';
import ZeroSaleBell, { useZeroSaleAlert } from './ZeroSaleBadge';
import WhatsNewBell from './WhatsNewBell';
import TaskBell from './tasks/TaskBell';
import WhatsNewModal from './WhatsNewModal';
import useWhatsNew from '../hooks/useWhatsNew';
import AllSections from './AllSections';
import BillingBanner from './BillingBanner';
import SupportBanner from './SupportBanner';
import { buildStaffNav, buildClientNav, WAREHOUSE_WORK_PATHS, isTestCompany, matchItem, matchRank, buildMenuEntries, searchSections, activeTabPath, SEARCH_ONLY_ITEMS } from './navConfig';
import GroupTabs from './GroupTabs';
import ErrorBoundary from './ErrorBoundary';
import SetupChecklist from './SetupChecklist';
import { setupFocusMode, SetupDetourBanner } from './setup/SetupGate';
import { useSetupStatus, resetSetupCache } from '../hooks/useSetupStatus';
import './Layout.css';
import { ROLE_LABELS } from '../utils/roles';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Таблицы-карточки на телефоне: подписи столбцов проставляются сами
  useTableCards([]);
  // Какие группы меню человек раскрыл — помним, чтобы не сворачивались каждый вход
  const [openSections, setOpenSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wms_nav_open') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('wms_nav_open', JSON.stringify(openSections)); } catch { /* приватный режим */ }
  }, [openSections]);
  // Бренд склада: у Full-Fix default — шапка как была; у склада облака — его название/логотип.
  // isCloud уходит в меню (buildStaffNav): раздел «Бренд и домен» только у склада облака.
  const { brand } = useBrand();
  // sellerCloud — склад облака без клиентов («Склад селлера»): свой магазин вместо «Клиентов»
  const authBase = useAuth();
  const auth = { ...authBase, isCloud: !brand.default, sellerCloud: !brand.default && !authBase.moduleOn('clients') };
  const { user, logout, isAdmin, isCompanyAdmin, isCompanyUser, isInternalStaff, companies, hasPermission, canFinance } = auth;
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('wms_sidebar_collapsed') === 'true';
  });

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('wms_sidebar_collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Пункт с параметрами активен, когда совпали адрес и его
  // параметры (пункт может вести на вкладку экрана).
  const isItemActive = (path) => {
    const [p, q] = path.split('?');
    if (location.pathname !== p) return false;
    if (!q) return true;
    const cur = new URLSearchParams(location.search);
    return [...new URLSearchParams(q)].every(([k, v]) => cur.get(k) === v);
  };

  // Состав меню — components/navConfig.js: пункт меню = процесс, внутри вкладки
  // (решение владельца 26.09). Здесь фильтруются ВКЛАДКИ (бывшие пункты) — по тем
  // же правилам, что и раньше; пункт процесса собирает buildMenuEntries.
  //
  // Фильтр по правам. Сверх флажков пункта — та же проверка, что у маршрута
  // (canOpen, utils/access.js): пункт, который маршрут не пустит, не показываем.
  const filterByPermissions = (sections) => sections.map(sec => ({
    ...sec,
    items: sec.items.filter(item => (!item.section || hasPermission(item.section)) && canOpen(item.path, auth)),
  })).filter(sec => sec.items.length > 0);

  // «Переучёт зоны» вернули в меню 29.09 (владелец: полная инвентаризация зоны).
  // Работающие разделы из меню не прячем (решение владельца 25.09).
  const HIDDEN_FROM_MENU = new Set([]);
  const dropHidden = (sections) => sections.map(sec => ({
    ...sec,
    items: sec.items.filter(item => !HIDDEN_FROM_MENU.has(item.path)),
  })).filter(sec => sec.items.length > 0);

  // Любой пользователь с компанией — клиент: ему клиентское меню, а не складское.
  // Пункты сотрудника клиента режутся по выбранным в карточке разделам, а пункты
  // уровня компании (ТЗ, финансы, маркетплейсы) видит только админ компании.
  const isClient = isCompanyAdmin || isCompanyUser;
  const clientNav = buildClientNav().map(sec => ({
    ...sec,
    items: sec.items.filter(item =>
      (isCompanyAdmin || (!item.companyAdminOnly && (!item.section || hasPermission(item.section))))
      && canOpen(item.path, auth)),
  })).filter(sec => sec.items.length > 0);
  // Полное меню человека — для окна «Все разделы» и поиска
  const fullSections = dropHidden(isClient ? clientNav : filterByPermissions(buildStaffNav(auth)));

  // Вкладки → пункты процессов. Все видимые адреса — чтобы действие «Создать
  // отгрузку» не стояло в меню, когда видна «Отгрузки» с этой кнопкой.
  const visiblePaths = new Set(fullSections.flatMap(sec => sec.items.map(i => i.path)));
  const fullMenu = buildMenuEntries(fullSections, { visiblePaths });

  // Сотруднику склада сверху — «Моя работа» (всегда раскрыта), остальное в
  // свёрнутых группах. Пункты «Моей работы» из групп ниже убраны — раньше они
  // стояли в меню дважды. В «Моей работе» — прежние названия экранов (was):
  // ТСД-сценарий кладовщика не меняется.
  const isWarehouseStaff = isInternalStaff && !isAdmin && !isClient;
  const byPath = {};
  fullSections.forEach(sec => sec.items.forEach(i => { byPath[i.path] = i; }));
  const workItems = isWarehouseStaff ? WAREHOUSE_WORK_PATHS.map(p => byPath[p]).filter(Boolean) : [];
  const workSet = new Set(workItems.map(i => i.path));
  let navSections;
  if (isClient) {
    navSections = fullMenu;
  } else if (workItems.length) {
    navSections = [
      { label: 'Моя работа', alwaysOpen: true,
        entries: workItems.map(t => ({ key: t.path, path: t.path, label: t.was || t.label, icon: t.icon, hint: t.hint, tabs: [t] })) },
      ...buildMenuEntries(fullSections, { visiblePaths, splitPaths: workSet })
        .map(sec => ({ ...sec, collapsedByDefault: true })),
    ];
  } else {
    // Администратор: раскрыта первая группа и та, где открытая страница
    navSections = fullMenu.map((sec, idx) => ({ ...sec, collapsedByDefault: idx > 0 }));
  }

  // Какая вкладка меню «владеет» открытым адресом — одно правило для подсветки
  // пункта и точки на свёрнутой группе: /products/5 → «Товары», /orders/create
  // и /scanning → «Отгрузки», /companies/2 → «Клиенты» (navConfig.activeTabPath).
  const menuTabPaths = navSections.flatMap(sec => sec.entries.flatMap(e => e.tabs.map(t => t.path)));
  const activePath = activeTabPath(location.pathname, location.search, menuTabPaths);
  const entryActive = (e) => e.tabs.some(t => t.path === activePath);
  // Меню длинное: при смене адреса докрутить его до активного пункта, иначе
  // открытый раздел внизу списка не виден. Крутим только само меню, не страницу.
  const navRef = useRef(null);
  useEffect(() => {
    const nav = navRef.current;
    const el = nav && nav.querySelector('.nav-item.active');
    if (!el) return;
    const n = nav.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top < n.top) nav.scrollTop += r.top - n.top - 8;
    else if (r.bottom > n.bottom) nav.scrollTop += r.bottom - n.bottom + 8;
  }, [location.pathname, activePath, sidebarOpen, sidebarCollapsed]);

  // Вкладки процесса над страницей: только на самом адресе вкладки (на
  // дочерних вроде /processing/123 — нет) и когда вкладок хотя бы две.
  // Берём из того меню, что показано человеку: у кладовщика «Приёмка» разбита
  // на пункты «Моей работы» — полоса с процессом целиком ему не нужна.
  // На ТСД экраны «Моей работы» тоже без полосы: строка вкладок съедает экран.
  const onTsd = typeof document !== 'undefined' && document.body.classList.contains('tsd');
  const groupEntry = navSections.flatMap(sec => sec.entries)
    .find(e => e.tabs.length > 1 && e.tabs.some(t => t.path.split('?')[0] === location.pathname)
      && !(onTsd && WAREHOUSE_WORK_PATHS.includes(location.pathname))) || null;
  // Поиск и «Все разделы» ищут по вкладкам: «Приёмка · Забор», прежнее название — ключом.
  // Сотруднику склада — ещё экраны не из меню (Сканирование, Лист подбора).
  const searchOnly = isClient ? [] : SEARCH_ONLY_ITEMS
    .filter(i => (!i.section || hasPermission(i.section)) && canOpen(i.path, auth) && !visiblePaths.has(i.path));
  const searchable = [
    ...searchSections(fullSections),
    ...(searchOnly.length ? [{ label: 'Другие экраны', items: searchSections([{ items: searchOnly }])[0].items }] : []),
  ];

  // ── Поиск по меню (Ctrl/⌘+K) ──
  const [query, setQuery] = useState('');
  const [hl, setHl] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);
  const searchRef = useRef(null);
  const closeMap = useCallback(() => setMapOpen(false), []);
  // Совпадение по названию — выше совпадения по подсказке (sort стабилен:
  // внутри одного ранга — порядок меню)
  const searchResults = query.trim()
    ? searchable.flatMap(sec => sec.items.filter(i => matchItem(i, query, sec.label)).map(i => ({ ...i, group: sec.label })))
      .map(i => ({ i, r: matchRank(i, query) })).sort((a, b) => a.r - b.r).map(x => x.i)
    : [];
  useEffect(() => { setHl(0); }, [query]);
  useEffect(() => { setQuery(''); setMapOpen(false); }, [location.pathname]);

  const isNarrow = () => typeof window !== 'undefined' && (
    window.matchMedia('(max-width: 640px)').matches || document.body.classList.contains('tsd'));
  const openSearch = useCallback(() => {
    if (isNarrow()) {
      setSidebarOpen(true);
      setTimeout(() => searchRef.current && searchRef.current.focus(), 60);
    } else if (sidebarCollapsed) {
      // Узкое меню не раскрываем без спроса — ищем в окне «Все разделы»
      setMapOpen(true);
    } else {
      searchRef.current && searchRef.current.focus();
    }
  }, [sidebarCollapsed]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.code === 'KeyK' || e.key === 'k' || e.key === 'л')) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSearch]);
  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHl(h => Math.min(h + 1, searchResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      const it = searchResults[hl] || searchResults[0];
      if (it) { e.preventDefault(); navigate(it.path); setQuery(''); e.currentTarget.blur(); }
    } else if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); }
  };
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

  // ── Шапка меню: вместо склейки всех 145 компаний — коротко (аудит №3, U15) ──
  const realCompanies = (companies || []).filter(c => !isTestCompany(c));
  let companyLine = null;
  let companyTitle;
  // Склад селлера (облако, без модуля «Клиенты»): компания одна — это сам магазин,
  // «Все клиенты склада · 1» ему непонятно. Название магазина и так в шапке.
  if (!user?.company_id) {
    if (realCompanies.length && !auth.sellerCloud) companyLine = `Все клиенты склада · ${realCompanies.length}`;
  } else if (realCompanies.length === 1) {
    companyLine = realCompanies[0].name;
  } else if (realCompanies.length > 1) {
    companyLine = `${realCompanies[0].name} и ещё ${realCompanies.length - 1}`;
    companyTitle = realCompanies.map(c => c.name).join(', ');
  }

  // «Нет продаж» — колокольчик с числом, один опрос на страницу
  const zeroSale = useZeroSaleAlert();
  // «Что нового»: пункты меню с меткой NEW (записи с nav, непрочитанные, свежие)
  const { nav: navNew } = useWhatsNew();
  const navNewSet = new Set(navNew || []);

  // Название текущего экрана — для верхней панели телефона/ТСД
  const allItems = fullSections.flatMap(sec => sec.items);
  const pageTitle = (() => {
    const exact = allItems.find(i => isItemActive(i.path));
    if (exact) return exact.was || exact.label;  // прежнее полное название: «Короба» без процесса непонятно
    const rule = ruleForUrl(location.pathname);
    if (rule) return rule.label;
    const prefix = allItems
      .filter(i => location.pathname.startsWith(i.path.split('?')[0] + '/'))
      .sort((x, y) => y.path.length - x.path.length)[0];
    return prefix ? (prefix.was || prefix.label) : brand.product_name;
  })();

  // Плашка «Нет доступа»: Guard (App.js) увёл сюда с закрытого адреса и передал
  // название раздела в state перехода. Закрыть — до следующего такого перехода.
  const deniedLabel = location.state?.denied || null;
  const [deniedClosedKey, setDeniedClosedKey] = useState(null);
  const showDenied = !!deniedLabel && deniedClosedKey !== location.key;

  // Облако: режим фокуса первого запуска — пока мастер не завершён и не пропущен,
  // администратор склада видит только мастер (components/setup/SetupGate.js).
  // Full-Fix (brand.default) статус не спрашивает — поведение как было.
  const setupActive = isAdmin && !brand.default && !user?.company_id;
  const { st: setupSt } = useSetupStatus(setupActive);
  const focusMode = setupActive ? setupFocusMode(setupSt, location) : null;
  const [logoWide, setLogoWide] = useState(false);

  const handleLogout = () => {
    resetSetupCache();
    logout();
    navigate(loginPath());   // терминал с бейджами — обратно на скан бейджа
  };

  // Пункт меню — процесс: активен, если открыта любая его вкладка
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = entryActive(item);
    return (
      <Link key={item.key} to={item.path} className={`nav-item ${isActive ? 'active' : ''}`}
            /* Подсказка объясняет складской жаргон: новому сотруднику не нужно спрашивать,
               что такое ФБС или ТЗ — достаточно задержать курсор */
            title={sidebarCollapsed ? item.label : (item.hint || undefined)}>
        <Icon size={17} />
        {!sidebarCollapsed && <span>{item.label}</span>}
        {(navNewSet.has(item.path) || (item.tabs || []).some((t) => navNewSet.has(t.path))) && !isActive && <span className="nav-new" title="Новое">{sidebarCollapsed ? '' : 'NEW'}</span>}
      </Link>
    );
  };

  const renderSection = (section) => {
    // Группа открыта, если это «Моя работа», меню свёрнуто в узкую полосу,
    // внутри активная страница, или человек сам её раскрыл. Явный выбор
    // человека главнее активной страницы.
    const hasActive = section.entries.some(entryActive);
    const stored = openSections[section.label];
    // Группа без названия (у клиента — «Главная») без заголовка и всегда открыта
    const unnamed = !section.label;
    const opened = section.alwaysOpen || unnamed || sidebarCollapsed
      || (stored !== undefined ? !!stored : (hasActive || !section.collapsedByDefault));
    return (
      <div key={section.label || 'main'} className="nav-section">
        {!sidebarCollapsed && !unnamed && (
          section.alwaysOpen ? (
            <div className="nav-section-label">{section.label}</div>
          ) : (
            <button
              type="button"
              className="nav-section-label nav-section-toggle"
              title={opened ? 'Свернуть группу' : 'Развернуть группу'}
              aria-expanded={opened}
              onClick={() => setOpenSections(prev => ({ ...prev, [section.label]: !opened }))}
            >
              <span>
                {section.label}
                {!opened && <span className="nav-section-count">{section.entries.length}</span>}
                {!opened && hasActive && <span className="nav-dot" title="Здесь открытая страница" />}
              </span>
              <ChevronDown size={13} className={opened ? 'nav-chevron nav-chevron--open' : 'nav-chevron'} />
            </button>
          )
        )}
        {sidebarCollapsed && <div className="nav-section-divider" />}
        {opened && section.entries.map(renderNavItem)}
      </div>
    );
  };

  if (focusMode === 'redirect') return <Navigate to="/setup" replace />;

  return (
    <div className={`layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Телефон и ТСД: верхняя панель в потоке страницы — кнопка меню, название
          экрана, колокольчик и «?». Раньше бургер и «?» висели поверх страницы и
          закрывали заголовок и первую строку (аудит №3, U6). Панель не липкая:
          на ТСД сверху прилипают поля скана, панель бы их закрывала. */}
      <header className="mobile-topbar">
        <button type="button" className="mobile-topbar__btn" aria-label="Открыть меню"
                onClick={() => setSidebarOpen(true)}>
          <Menu size={22} />
        </button>
        <div className="mobile-topbar__title" title={pageTitle}>{pageTitle}</div>
        <WhatsNewBell variant="topbar" />
        <TaskBell variant="topbar" />
        <ZeroSaleBell data={zeroSale} align="right" variant="topbar" />
        <SwitchUserButton variant="topbar" />
        <HelpButton inline />
      </header>
      <WhatsNewModal />
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {sidebarCollapsed ? (
            <div className="logo-icon">{brand.default ? 'FF' : brand.short_name}</div>
          ) : brand.default ? (
            <div className="sidebar-logo">
              <div className="sidebar-logo-box"><Package size={16} /></div>
              <span className="sidebar-logo-text">Full-<span>Fix</span></span>
            </div>
          ) : (
            <div className="sidebar-logo" title={brand.product_name}>
              {brand.logo_url
                ? <img src={brand.logo_url} alt={logoWide ? brand.product_name : ''}
                       className={`sidebar-logo-img ${logoWide ? '' : 'sidebar-logo-img--square'}`}
                       onLoad={(e) => setLogoWide(e.currentTarget.naturalWidth > 1.8 * e.currentTarget.naturalHeight)} />
                : <div className="sidebar-logo-box"><Package size={16} /></div>}
              {/* Квадратный логотип — значок, название склада рядом; широкий (с надписью) — сам по себе */}
              {!(brand.logo_url && logoWide) && <span className="sidebar-logo-text sidebar-logo-text--brand">{brand.product_name}</span>}
            </div>
          )}
          {!sidebarCollapsed && (
            <div className="sidebar-header__tools">
              <WhatsNewBell variant="sidebar" />
              <TaskBell variant="sidebar" />
              <ZeroSaleBell data={zeroSale} align="left" variant="sidebar" />
              <button type="button" className="sidebar-close" aria-label="Закрыть меню"
                      onClick={() => setSidebarOpen(false)}>
                <X size={20} />
              </button>
            </div>
          )}
        </div>
        {companyLine && !sidebarCollapsed && (
          <div className="multi-company-info" title={companyTitle}>{companyLine}</div>
        )}
        {!sidebarCollapsed ? (
          <div className="nav-search">
            <Search size={15} className="nav-search__icon" />
            <input
              ref={searchRef}
              className="nav-search__input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Найти раздел"
              aria-label="Поиск по меню"
            />
            {query ? (
              <button type="button" className="nav-search__clear" aria-label="Очистить" onClick={() => setQuery('')}>
                <X size={14} />
              </button>
            ) : (
              <kbd className="nav-search__kbd" title="Быстрый поиск раздела">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
            )}
          </div>
        ) : (
          <button type="button" className="nav-search-mini" title={`Найти раздел (${isMac ? '⌘K' : 'Ctrl+K'})`}
                  onClick={() => setMapOpen(true)}>
            <Search size={17} />
          </button>
        )}
        <div className="sidebar-balance">
          {/* Баланс — деньги компании: сотруднику клиента с ограниченными разделами
              (например, «только приёмки») его не показываем, как и раздел «Финансы». */}
          {(!isCompanyUser || isCompanyAdmin) && <BalanceBadge collapsed={sidebarCollapsed} />}
          {/* В узком меню колокольчику нет места в шапке — он здесь */}
          {sidebarCollapsed && <ZeroSaleBell data={zeroSale} align="left" variant="sidebar" />}
        </div>
        <nav className="sidebar-nav" ref={navRef}>
          {query.trim() ? (
            <div className="nav-search-results">
              {searchResults.length === 0 ? (
                <div className="nav-search-empty">
                  Ничего не нашлось.
                  <button type="button" className="nav-search-empty__all" onClick={() => setMapOpen(true)}>
                    Открыть все разделы
                  </button>
                </div>
              ) : searchResults.map((item, i) => {
                const Icon = item.icon;
                return (
                  <Link key={`${item.group}:${item.path}`} to={item.path}
                        className={`nav-item nav-item--found ${i === hl ? 'nav-item--hl' : ''}`}
                        title={item.hint || undefined}
                        onMouseEnter={() => setHl(i)}>
                    <Icon size={17} />
                    <span className="nav-item__text">
                      <span>{item.label}</span>
                      {item.group && <span className="nav-item__group">{item.group}</span>}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <>
              {navSections.map(renderSection)}
              <button type="button" className="nav-item nav-item--all" onClick={() => setMapOpen(true)}
                      title={sidebarCollapsed ? 'Все разделы' : 'Все экраны, доступные вам, одним списком'}>
                <LayoutGrid size={17} />
                {!sidebarCollapsed && <span>Все разделы</span>}
              </button>
            </>
          )}
        </nav>
        <button className="sidebar-collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}>
          {sidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          {!sidebarCollapsed && <span>Свернуть</span>}
        </button>
        <div className="sidebar-footer">
          <SwitchUserButton collapsed={sidebarCollapsed} />
          <div className="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {!sidebarCollapsed && (
              <div className="user-details">
                <span className="user-name" title={user?.name}>{user?.name}</span>
                <span className="user-role">{ROLE_LABELS[user?.role] || 'Сотрудник'}</span>
              </div>
            )}
          </div>
          <div className="sidebar-actions">
            <GlobalScan />
            <button className="btn-theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!sidebarCollapsed && (
              <>
                <button className="btn-theme-toggle" onClick={() => navigate('/profile')} title="Профиль">
                  <Settings2 size={16} />
                </button>
                <button className="btn-logout" onClick={handleLogout} title="Выйти">
                  <LogOut size={18} />
                </button>
              </>
            )}
          </div>
          {!brand.default && brand.powered_by && !sidebarCollapsed && (
            <a className="sidebar-powered" href={brand.powered_by_url} target="_blank" rel="noopener noreferrer">
              {brand.powered_by_text}
            </a>
          )}
        </div>
      </aside>
      <main className="main-content">
        <div className="page-frame">
          {showDenied && (
            <div className="alert alert-warning layout-denied" role="alert">
              <AlertTriangle size={18} className="layout-denied__icon" />
              <div className="layout-denied__text">
                {location.state?.deniedModule
                  ? <>Раздел «{deniedLabel}» выключен: его нет в тарифе или он отключён в «Каталоге модулей».</>
                  : <>Нет доступа к разделу «{deniedLabel}». Попросите руководителя открыть доступ.</>}
              </div>
              <button type="button" className="layout-denied__close" aria-label="Закрыть"
                      onClick={() => setDeniedClosedKey(location.key)}>
                <X size={16} />
              </button>
            </div>
          )}
          {/* Упавшая страница не роняет меню: ошибка ловится здесь, а не выше Layout.
              Смена адреса сбрасывает ошибку (resetKey, не key: key пересоздал бы
              страницу при каждом переходе, например /products → /products/5).
              Свой Suspense — пока догружается кусок страницы, меню остаётся на месте. */}
          {isAdmin && !brand.default && <BillingBanner />}
          {!brand.default && <SupportBanner />}
          {focusMode === 'detour' && <SetupDetourBanner />}
          {groupEntry && <GroupTabs entry={groupEntry} />}
          {/* Облако Н3: «Готовность склада» на домашнем экране админа нового склада,
              первый вход — в мастер. У Full-Fix (brand.default) не запрашивается. */}
          {isAdmin && !brand.default && (
            <SetupChecklist enabled isHome={location.pathname === homePath(auth).split('?')[0]} />
          )}
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<div className="page-loading"><span className="spinner" /></div>}>
              {children}
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <HelpButton />
      {mapOpen && <AllSections sections={searchable} onClose={closeMap} />}
      <VersionWatcher />
    </div>
  );
}

export default Layout;
